// React
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Components
import SnackBar from '../../../../components/Shared/Utils/SnackBar';
import Loader from '../../../../components/Shared/Utils/Loader';
import { Dialog, MpsSectionedForm } from '@mobilitystore/components-interface';

// Actions
import {
  getRegionSpecificCountryList,
  getCountryList
} from '../../../../actions/CountryAction';
import { createPickupAddress } from '../../../../actions/user-service-action';
import { setSnackbar } from '../../../../actions/generic-action';

// Utils
import { getCountryOptions } from '../../../dashboard/UnifiedSellerDashboard/utils';
import {
  handleInputChange,
  getDefaultIsdCodeByRegion,
  pickupSectionForm,
  DialogButtons
} from './utils';

// Data
import { pickupDynamicFields } from './data';
import idConstants from '../../../../data/idConstants';

// Css
import './index.scss';

const CapturePickupAddress = ({ onClose }) => {
  const dispatch = useDispatch();
  const [pickupAddressDetails, setPickupAddressDetails] = useState({});
  const [pickupAddressFormErrors, setPickupAddressFormErrors] = useState({});
  const isdCodeDefaultSet = useRef(false);

  const {
    regionSpecificCountryList,
    countryList,
    content,
    userCompanyStatus,
    snackbarStatus,
    isLoading
  } = useSelector((state) => ({
    regionSpecificCountryList: state.CountryReducer.regionSpecificCountryList,
    countryList: state.CountryReducer.countryList,
    content: state.ContentReducer.content,
    userCompanyStatus: state.CompanyDetailsReducer.userCompanyStatus,
    snackbarStatus: state.GenericReducer.snackbarStatus,
    isLoading: state.OrderSummaryReducer.isLoading
  }));

  const pickupAddressContent = content?.components?.my_profile?.pickup_address;

  useEffect(() => {
    dispatch(getRegionSpecificCountryList);
    dispatch(getCountryList(idConstants.typeOfUsage.contactNumber));
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_REGION === 'IN' && regionSpecificCountryList?.length) {
      const indiaOption = getCountryOptions(
        regionSpecificCountryList,
        'countryName'
      ).find((opt) => opt.id === 'IN');
      if (indiaOption) {
        setPickupAddressDetails((prev) => ({ ...prev, country: indiaOption }));
      }
    }
  }, [regionSpecificCountryList]);

  useEffect(() => {
    if (
      countryList?.length &&
      !isdCodeDefaultSet.current &&
      !pickupAddressDetails.isdCode
    ) {
      const { isdCode } = getDefaultIsdCodeByRegion(countryList);
      if (isdCode) {
        setPickupAddressDetails((prev) => ({ ...prev, isdCode: isdCode }));
        isdCodeDefaultSet.current = true;
      }
    }
  }, [countryList]);

  const requiredFields = [];
  pickupDynamicFields?.forEach((section) => {
    section?.fields?.forEach((field) => {
      if (field.type === 'COMBINE' && Array.isArray(field.attributes)) {
        field.attributes.forEach((attribute) => {
          if (attribute.validation?.rule?.required?.isRequired) {
            requiredFields.push(attribute.name);
          }
        });
      } else if (field.validation?.rule?.required?.isRequired) {
        requiredFields.push(field.name);
      }
    });
  });

  const isFormValid = (() => {
    const allRequiredFieldsFilled = requiredFields.every((field) => {
      const value = pickupAddressDetails[field];
      const error = pickupAddressFormErrors[field];
      const isFilled =
        value !== undefined && value !== null && value.toString().trim() !== '';
      const isValid = isFilled && !error;
      return isValid;
    });

    return allRequiredFieldsFilled;
  })();

  const handleSave = () => {
    let payload = { ...pickupAddressDetails };
    if (payload.isdCode && payload.contactNo) {
      const { isdCode, contactNo, ...rest } = payload;
      payload = {
        ...rest,
        contactNo: `${isdCode} ${contactNo}`
      };
    }
      if (payload.pincode) {
        payload.postalCode = payload.pincode;
        delete payload.pincode;
      }
    if (process.env.REACT_APP_REGION === 'IN') {
      const indiaOption = getCountryOptions(
        regionSpecificCountryList,
        'countryName'
      ).find((opt) => opt.id === 'IN');
      if (indiaOption) payload.country = indiaOption.label;
    }
    if (userCompanyStatus?.companyId) {
      payload.companyId = userCompanyStatus.companyId;
    }

    dispatch(createPickupAddress(payload)).then((response) => {
      if (response) {
        dispatch(
          setSnackbar({
            showSnackbar: true,
            message: pickupAddressContent.success_message,
            severity: 'success'
          })
        );
        setPickupAddressDetails({});
        setTimeout(() => {
          onClose();
          dispatch(setSnackbar({ showSnackbar: false, message: '' }));
        }, 1000);
      }
    });
  };

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  const handleFormInputChange = (event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    return handleInputChange(
      event,
      setPickupAddressDetails,
      setPickupAddressFormErrors
    );
  };
  return (
    <div className='capture-pickup-address__dialog-popup'>
      <Dialog
        show={true}
        showPopup={true}
        className='capture-pickup-address-dialog capture-pickup-address__dialog'
        onClose={handleDialogClose}
        showCloseIcon={true}
        buttons={DialogButtons(content, onClose, handleSave, isFormValid)}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        maxWidth='md'
        fullWidth={true}
        BackdropProps={{
          className: 'capture-pickup-address__backdrop',
          onClick: (event) => event.stopPropagation()
        }}
        PaperProps={{
          className: 'capture-pickup-address__paper',
          onClick: (event) => event.stopPropagation()
        }}
        container={() => document.body}
        disablePortal={false}
      >
        {isLoading && <Loader />}
        <div
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className='capture-pickup-address__form-container'
        >
          <MpsSectionedForm
            sections={pickupSectionForm(
              pickupDynamicFields,
              pickupAddressDetails,
              pickupAddressFormErrors,
              countryList,
              regionSpecificCountryList,
              getCountryOptions
            )}
            values={pickupAddressDetails}
            errors={pickupAddressFormErrors}
            isFormValid={isFormValid}
            handleInputChange={handleFormInputChange}
          />
        </div>
        {snackbarStatus?.showSnackbar && (
          <SnackBar
            severity={snackbarStatus?.severity}
            changeState={() =>
              dispatch(
                setSnackbar({
                  showSnackbar: false,
                  message: ''
                })
              )
            }
            message={snackbarStatus?.message}
          />
        )}
      </Dialog>
    </div>
  );
};

CapturePickupAddress.propTypes = {
  onClose: PropTypes.func
};

export default CapturePickupAddress;
